import { useEffect, useState } from 'react'
import { deleteAppointment, saveAppointment, type Appointment } from '@core/appointments'
import { loadFamily, type FamilyMember, type FamilySession } from '@core/session'
import { Button, ErrorNote, Field } from '@design/components'
import { todayIso } from '@core/dates'
import { Overlay } from '@design/Overlay'

/**
 * Making or changing an appointment.
 *
 * Two required things — a name and a day — and everything else optional. The
 * hour is optional on purpose: "zondag komt mijn zus" is a real appointment,
 * and a form that insists on a time would make you invent 00:00 and then show
 * it back to you as if you had meant it.
 *
 * Who is going is not decoration. It decides who can *see* the appointment at
 * all (0038): a member reads only the ones they are on. So the default is you
 * alone — the safe direction — and adding the family is one tap.
 */
export function AppointmentForm({
  session,
  initial,
  onDone,
  onCancel,
}: {
  session: FamilySession
  initial?: Appointment | null
  onDone: () => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [onDate, setOnDate] = useState(initial?.onDate ?? todayIso())
  const [startTime, setStartTime] = useState(initial?.startTime ?? '')
  const [endTime, setEndTime] = useState(initial?.endTime ?? '')
  const [location, setLocation] = useState(initial?.location ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [people, setPeople] = useState<string[]>(initial?.people ?? [session.userId])
  const [spaceId, setSpaceId] = useState(initial?.spaceId ?? session.spaces[0]?.id ?? '')
  const [family, setFamily] = useState<FamilyMember[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    loadFamily(session.accountId).then(setFamily).catch(() => setFamily([]))
  }, [session.accountId])

  function toggle(userId: string) {
    setPeople((now) =>
      now.includes(userId) ? now.filter((u) => u !== userId) : [...now, userId],
    )
  }

  async function save() {
    if (!title.trim()) {
      setError('Geef de afspraak een naam.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await saveAppointment({
        id: initial?.id ?? null,
        spaceId,
        title,
        onDate,
        startTime: startTime || null,
        endTime: endTime || null,
        location: location || null,
        notes: notes || null,
        people,
      })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan lukte niet.')
      setBusy(false)
    }
  }

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <button onClick={onCancel} className="text-muted px-2 py-2">
          Annuleren
        </button>
        <span className="font-medium">{initial ? 'Afspraak' : 'Nieuwe afspraak'}</span>
        <span className="w-20" />
      </header>

      <div className="flex-1 scroll-area px-4 py-4">
        <Field
          label="Wat?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Tandarts"
          autoFocus
        />

        <Field
          label="Wanneer"
          type="date"
          value={onDate}
          onChange={(e) => setOnDate(e.target.value)}
        />

        <div className="flex gap-3">
          <div className="flex-1">
            <Field
              label="Van (mag leeg)"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <Field
              label="Tot"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>

        <Field
          label="Waar? (mag leeg)"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Bij de tandarts"
        />

        <p className="text-sm text-muted mb-1.5">Wie gaat er heen?</p>
        <div className="flex flex-wrap gap-2 mb-2">
          {family.map((member) => (
            <button
              key={member.userId}
              onClick={() => toggle(member.userId)}
              className={`min-h-[2.75rem] px-4 rounded-xl border text-sm font-medium ${
                people.includes(member.userId)
                  ? 'border-ink bg-ink/5'
                  : 'border-line text-muted'
              }`}
            >
              {member.userId === session.userId ? 'Ik' : member.displayName}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted mb-4">
          Alleen wie hier staat ziet deze afspraak, en krijgt de avond ervoor een
          bericht.
        </p>

        {session.spaces.length > 1 && (
          <>
            <p className="text-sm text-muted mb-1.5">Waarvoor?</p>
            <div className="flex gap-2 mb-4">
              {session.spaces.map((space) => (
                <button
                  key={space.id}
                  onClick={() => setSpaceId(space.id)}
                  className={`flex-1 min-h-[3rem] rounded-xl border text-sm font-medium ${
                    space.id === spaceId ? 'border-ink bg-ink/5' : 'border-line text-muted'
                  }`}
                >
                  {space.name}
                </button>
              ))}
            </div>
          </>
        )}

        <Field
          label="Notitie (mag leeg)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <ErrorNote>{error}</ErrorNote>

        {initial && (
          <div className="mt-6 pt-4 border-t border-line">
            {!confirming ? (
              <button
                onClick={() => setConfirming(true)}
                className="w-full py-3 text-sm text-muted"
              >
                Afspraak weghalen
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  className="flex-1 min-h-[2.75rem] rounded-xl border border-line text-sm"
                >
                  Toch niet
                </button>
                <button
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true)
                    try {
                      await deleteAppointment(initial.id)
                      onDone()
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Weghalen lukte niet.')
                      setBusy(false)
                    }
                  }}
                  className="flex-1 min-h-[2.75rem] rounded-xl border border-ink bg-ink/5 text-sm font-medium"
                >
                  Weghalen
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line">
        <Button size="large" onClick={save} disabled={busy}>
          {busy ? 'Bezig…' : 'Bewaren'}
        </Button>
      </div>
    </Overlay>
  )
}
